/**
 * Shared validation utilities
 * Eliminates duplicate validation logic across forms
 */

/**
 * Email validation regex pattern
 * Used consistently across all forms requiring email validation
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate an email address
 * @returns Error message if invalid, null if valid
 */
export function validateEmail(value: string | undefined, required = true): string | null {
  if (!value?.trim()) {
    return required ? 'Email is required' : null;
  }
  if (!EMAIL_REGEX.test(value)) {
    return 'Invalid email format';
  }
  return null;
}

/**
 * Validate a required string field
 * @returns Error message if invalid, null if valid
 */
export function validateRequired(value: string | undefined, fieldName: string): string | null {
  if (!value?.trim()) {
    return `${fieldName} is required`;
  }
  return null;
}

/**
 * Validate a partner code (2-4 uppercase letters)
 * @returns Error message if invalid, null if valid
 */
export function validatePartnerCode(value: string | undefined): string | null {
  if (!value?.trim()) {
    return 'Partner code is required';
  }
  if (!/^[A-Za-z]{2,4}$/.test(value)) {
    return 'Code must be 2-4 letters';
  }
  return null;
}

/**
 * Validate a URL (optional)
 * @returns Error message if invalid, null if valid or empty
 */
export function validateUrl(value: string | undefined): string | null {
  if (!value?.trim()) return null;
  try {
    new URL(value);
    return null;
  } catch {
    return 'Invalid URL format';
  }
}

/**
 * Create Mantine form validation object from validation functions
 */
export const formValidators = {
  email: (required = true) => (value: string | undefined) => validateEmail(value, required),
  required: (fieldName: string) => (value: string | undefined) => validateRequired(value, fieldName),
  partnerCode: (value: string | undefined) => validatePartnerCode(value),
  url: (value: string | undefined) => validateUrl(value),
};
